import { z } from 'zod';
/**
 * The player's settings (ADR 0029), saved on the account so they follow the
 * player from one browser to the next. Presentation only: volumes, keys and
 * display switches. The server stores and returns them, it never reads them.
 *
 * Every field is optional so a client from before a setting existed still
 * saves, and `strict` so nothing else rides along in the account row.
 */
/** Every action a key can be bound to; the client names them in French. */
export const KEY_ACTION_IDS = [
    'inventory',
    'map',
    'travel',
    'profile',
    'achievements',
    'quests',
    'spellbook',
    'settings',
    'shop',
    'friends',
    'guild',
    'ladder',
    'bestiary',
    'auction',
    'mount',
    'potion',
    'target',
    'targetNext',
    'interact',
    'spell1',
    'spell2',
    'spell3',
    'spell4',
    'walkUp',
    'walkDown',
    'walkLeft',
    'walkRight',
];
export const KeyActionSchema = z.enum(KEY_ACTION_IDS);
/**
 * A key, either by the character it types (a letter, the same on AZERTY and
 * QWERTY) or by its position (`event.code`: a digit, Space, an arrow).
 */
export const KeyBindingSchema = z.union([
    z.object({ key: z.string().min(1).max(8) }).strict(),
    z.object({ code: z.string().min(1).max(32) }).strict(),
]);
const VolumeSchema = z.number().min(0).max(1);
/** The languages the game speaks (ADR 0069): French is the source, English its translation. */
export const LANGUAGES = ['fr', 'en'];
export const LanguageSchema = z.enum(LANGUAGES);
export const PlayerSettingsSchema = z
    .object({
    muted: z.boolean(),
    sfxVolume: VolumeSchema,
    musicVolume: VolumeSchema,
    keys: z.partialRecord(KeyActionSchema, KeyBindingSchema),
    damageNumbers: z.boolean(),
    combatFrame: z.boolean(),
    minimap: z.boolean(),
    /** A short buzz on a blow received, on a phone that can (ADR 0043). */
    vibration: z.boolean(),
    /** The ping meter under the top buttons. */
    ping: z.boolean(),
    /** The screen's jolt on a critical hit, a heavy blow or a level (ADR 0049). */
    screenShake: z.boolean(),
    /** The red vignette and the heartbeat under a quarter of the health. */
    lowHealthAlert: z.boolean(),
    /** The chat panel, hidden while the player is not typing. */
    chat: z.boolean(),
    /** The night's darkening and, later, the weather on the screen (ADR 0093). */
    nightAndWeather: z.boolean(),
    /** The interface's language (ADR 0069). */
    language: LanguageSchema,
})
    .partial()
    .strict();
/** What `GET /account/settings` answers: null until the account has saved once. */
export const PlayerSettingsResponseSchema = z.object({
    settings: PlayerSettingsSchema.nullable(),
});
//# sourceMappingURL=settings.js.map